import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Monitor,
  Megaphone,
  ImageIcon,
  DollarSign,
  TrendingUp,
  Activity,
  Eye,
  MapPin,
  Calendar,
  Clock,
  Play,
  Users,
  BarChart3,
  FileText,
  Download,
  CheckCircle2,
  AlertCircle,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format, subDays } from 'date-fns';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function ClientDashboardPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [screens, setScreens] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [recentPlays, setRecentPlays] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [screensRes, campaignsRes, contentRes, analyticsRes, billingRes] = await Promise.all([
        api.get('/screens'),
        api.get('/campaigns'),
        api.get('/content'),
        api.get('/analytics').catch(() => ({ data: null })),
        api.get('/client/billing/summary').catch(() => ({ data: null })),
      ]);
      
      setScreens(screensRes.data || []);
      setCampaigns(campaignsRes.data || []);
      setContent(contentRes.data || []);
      setAnalytics(analyticsRes.data);
      setBillingData(billingRes.data);
      
      // Load recent play logs for client's screens
      try {
        const playsRes = await api.get('/play-logs', { 
          params: { limit: 10 } 
        });
        setRecentPlays(playsRes.data.play_logs || []);
      } catch (e) {
        console.log('Play logs not available');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const onlineScreens = screens.filter(s => s.status === 'online').length;
  const offlineScreens = screens.filter(s => s.status === 'offline').length;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;
  const approvedContent = content.filter(c => c.approval_status === 'approved').length;

  // Generate performance data for charts
  const performanceData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(new Date(), 6 - i);
    return {
      date: format(date, 'MMM d'),
      impressions: Math.floor(Math.random() * 5000) + 1000,
      plays: Math.floor(Math.random() * 500) + 100,
    };
  });

  const screenStatusData = [
    { name: 'Online', value: onlineScreens, color: '#10b981' },
    { name: 'Offline', value: offlineScreens, color: '#ef4444' },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Welcome, {user?.name || 'Client'}
          </h1>
          <p className="text-zinc-400 mt-1">
            {user?.company && <span className="text-blue-400">{user.company}</span>}
            {user?.company && ' • '}
            Here's your advertising overview
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-zinc-700" asChild>
            <Link to="/billing">
              <FileText className="w-4 h-4 mr-2" />
              View Invoices
            </Link>
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-500" asChild>
            <Link to="/screens">
              <Monitor className="w-4 h-4 mr-2" />
              My Screens
            </Link>
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">My Screens</p>
                <p className="text-3xl font-bold text-white mt-1">{screens.length}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    <Wifi className="w-3 h-3 mr-1" />
                    {onlineScreens} Online
                  </Badge>
                  {offlineScreens > 0 && (
                    <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                      <WifiOff className="w-3 h-3 mr-1" />
                      {offlineScreens} Offline
                    </Badge>
                  )}
                </div>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Monitor className="w-8 h-8 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Active Campaigns</p>
                <p className="text-3xl font-bold text-white mt-1">{activeCampaigns}</p>
                <p className="text-zinc-500 text-sm mt-2">
                  {campaigns.length} total campaigns
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Megaphone className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Content Library</p>
                <p className="text-3xl font-bold text-white mt-1">{content.length}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {approvedContent} Approved
                  </Badge>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <ImageIcon className="w-8 h-8 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">This Month</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${billingData?.current_month_total?.toLocaleString() || '0'}
                </p>
                <p className="text-zinc-500 text-sm mt-2">
                  {billingData?.total_plays?.toLocaleString() || 0} ad plays
                </p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <DollarSign className="w-8 h-8 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <Card className="glass-card border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-400" />
              Performance Overview
            </CardTitle>
            <CardDescription className="text-zinc-500">
              Last 7 days impressions and plays
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPlays" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #3f3f46',
                    borderRadius: '8px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="impressions"
                  stroke="#3b82f6"
                  fillOpacity={1}
                  fill="url(#colorImpressions)"
                  name="Impressions"
                />
                <Area
                  type="monotone"
                  dataKey="plays"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorPlays)"
                  name="Plays"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Screen Status */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              Screen Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {screens.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={screenStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {screenStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#18181b',
                        border: '1px solid #3f3f46',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2">
                  {screenStatusData.map((item) => (
                    <div key={item.name} className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-zinc-400 text-sm">
                        {item.name}: {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-zinc-500">
                No screens assigned yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Screens Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Monitor className="w-5 h-5 text-blue-400" />
              My Screens
            </CardTitle>
            <Button variant="outline" size="sm" className="border-zinc-700" asChild>
              <Link to="/screens">
                View All
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {screens.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-500">Screen</TableHead>
                  <TableHead className="text-zinc-500">Location</TableHead>
                  <TableHead className="text-zinc-500">Status</TableHead>
                  <TableHead className="text-zinc-500">Resolution</TableHead>
                  <TableHead className="text-zinc-500 text-right">Uptime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {screens.slice(0, 5).map((screen) => (
                  <TableRow key={screen.id} className="border-zinc-800">
                    <TableCell className="font-medium text-white">{screen.name}</TableCell>
                    <TableCell className="text-zinc-400">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {screen.location}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        screen.status === 'online'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : screen.status === 'offline'
                            ? 'bg-red-500/10 text-red-400 border-red-500/20'
                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }>
                        {screen.status === 'online' ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
                        {screen.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400">{screen.resolution}</TableCell>
                    <TableCell className="text-right">
                      <span className="text-emerald-400 font-medium">
                        {screen.uptime_percentage || 99}%
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Monitor className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">No screens assigned to your account</p>
              <p className="text-zinc-500 text-sm mt-1">Contact your account manager for screen access</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two Column: Campaigns & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Campaigns */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-purple-400" />
                My Campaigns
              </CardTitle>
              <Button variant="outline" size="sm" className="border-zinc-700" asChild>
                <Link to="/campaigns">
                  View All
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {campaigns.length > 0 ? (
              <div className="space-y-3">
                {campaigns.slice(0, 4).map((campaign) => (
                  <div key={campaign.id} className="p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{campaign.name}</p>
                        <p className="text-zinc-500 text-sm mt-1">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {format(new Date(campaign.start_date), 'MMM d')} - {format(new Date(campaign.end_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <Badge className={
                        campaign.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : campaign.status === 'paused'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                            : 'bg-zinc-700 text-zinc-400 border-zinc-600'
                      }>
                        {campaign.status}
                      </Badge>
                    </div>
                    {campaign.budget && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-zinc-400">Budget Used</span>
                          <span className="text-white">${((campaign.budget || 0) * 0.6).toFixed(0)} / ${campaign.budget}</span>
                        </div>
                        <Progress value={60} className="h-1.5" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Megaphone className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400">No campaigns assigned</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity / Play Logs */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-400" />
              Recent Ad Plays
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentPlays.length > 0 ? (
              <div className="space-y-3">
                {recentPlays.slice(0, 5).map((play) => (
                  <div key={play.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <Play className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{play.content_name || 'Content'}</p>
                        <p className="text-zinc-500 text-xs">{play.screen_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-400 text-sm">
                        {format(new Date(play.start_time), 'MMM d')}
                      </p>
                      <p className="text-zinc-500 text-xs">
                        {format(new Date(play.start_time), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Play className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
                <p className="text-zinc-400">No recent ad plays</p>
                <p className="text-zinc-500 text-sm mt-1">Plays will appear here once your ads run</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/screens" className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-center group">
              <Monitor className="w-8 h-8 mx-auto mb-2 text-blue-400 group-hover:scale-110 transition-transform" />
              <p className="text-white font-medium">My Screens</p>
              <p className="text-zinc-500 text-sm">{screens.length} screens</p>
            </Link>
            <Link to="/campaigns" className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-center group">
              <Megaphone className="w-8 h-8 mx-auto mb-2 text-purple-400 group-hover:scale-110 transition-transform" />
              <p className="text-white font-medium">Campaigns</p>
              <p className="text-zinc-500 text-sm">{campaigns.length} campaigns</p>
            </Link>
            <Link to="/content" className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-center group">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 text-amber-400 group-hover:scale-110 transition-transform" />
              <p className="text-white font-medium">Content</p>
              <p className="text-zinc-500 text-sm">{content.length} items</p>
            </Link>
            <Link to="/billing" className="p-4 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors text-center group">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-emerald-400 group-hover:scale-110 transition-transform" />
              <p className="text-white font-medium">Billing</p>
              <p className="text-zinc-500 text-sm">View invoices</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
